
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import TopNavigation from '../../../components/feature/TopNavigation';
import Button from '../../../components/base/Button';

interface BiddingOffer {
  id: string;
  driverName: string;
  rating: number;
  carModel: string;
  price: number;
  estimatedTime: number;
  profileImage?: string;
}

export default function BiddingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isWaiting, setIsWaiting] = useState(true);
  const [offers, setOffers] = useState<BiddingOffer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  
  const bookingData = location.state || {
    pickup: 'الرياض، حي الملز',
    destination: 'الرياض، حي النخيل'
  };

  useEffect(() => {
    // Simulate receiving offers
    const timer = setTimeout(() => {
      setIsWaiting(false);
      setOffers([
        {
          id: '1',
          driverName: 'أحمد محمد',
          rating: 4.8,
          carModel: 'تويوتا كامري 2022',
          price: 22,
          estimatedTime: 15
        },
        {
          id: '2',
          driverName: 'محمد علي',
          rating: 4.9,
          carModel: 'هيونداي إلنترا 2023',
          price: 18,
          estimatedTime: 12
        },
        {
          id: '3',
          driverName: 'عبدالله سعد',
          rating: 4.7,
          carModel: 'نيسان التيما 2021',
          price: 25,
          estimatedTime: 18
        }
      ]);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const handleSelectOffer = (offerId: string) => {
    setSelectedOffer(offerId);
  };

  const handleConfirmOffer = () => {
    if (!selectedOffer) return;
    
    const selectedOfferData = offers.find(offer => offer.id === selectedOffer);
    if (selectedOfferData) {
      navigate('/trip/tracking', {
        state: {
          ...bookingData,
          price: selectedOfferData.price,
          driver: {
            name: selectedOfferData.driverName,
            rating: selectedOfferData.rating,
            phone: '+966501234567',
            carModel: selectedOfferData.carModel,
            plateNumber: 'أ ب ج 1234'
          }
        }
      });
    }
  };

  if (isWaiting) {
    return (
      <div className="min-h-screen bg-[#FDFDFD]">
        <TopNavigation 
          title="طلب مزايدة" 
          showBack 
          onBack={() => navigate('/booking')}
        />
        
        <div className="pt-20 px-4 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-[#F9A826]/20 to-[#E8971F]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-16 h-16 border-4 border-[#F9A826] border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">جاري إرسال طلبك</h2>
            <p className="text-gray-600 mb-4">يتم الآن إرسال طلبك لجميع السائقين المتاحين</p>
            <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse">
              <div className="w-2 h-2 bg-[#F9A826] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-[#F9A826] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-[#F9A826] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation 
        title="العروض المستلمة" 
        showBack 
        onBack={() => navigate('/booking')}
      />
      
      <div className="pt-20 px-4">
        {/* Trip Info */}
        <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">تفاصيل الرحلة</h3>
            <div className="bg-green-100 px-3 py-1 rounded-full">
              <span className="text-green-600 text-sm font-medium">{offers.length} عروض</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3"></div>
              <div>
                <p className="text-xs text-gray-500">من</p>
                <p className="text-sm font-medium text-gray-800">{bookingData.pickup}</p>
              </div>
            </div>
            
            <div className="border-r border-gray-200 mr-1.5 h-6"></div>
            
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3"></div>
              <div>
                <p className="text-xs text-gray-500">إلى</p>
                <p className="text-sm font-medium text-gray-800">{bookingData.destination}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Offers List */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">اختر أفضل عرض</h3>
          <div className="space-y-3">
            {offers.map((offer) => (
              <div
                key={offer.id}
                onClick={() => handleSelectOffer(offer.id)}
                className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition-all duration-200 ${
                  selectedOffer === offer.id
                    ? 'border-[#F9A826] bg-[#F9A826]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0056A6]/10 to-[#F9A826]/10 rounded-full flex items-center justify-center mr-4">
                      <i className="ri-user-line text-[#0056A6] text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">{offer.driverName}</h4>
                      <p className="text-sm text-gray-600">{offer.carModel}</p>
                      <div className="flex items-center mt-1">
                        <i className="ri-star-fill text-yellow-400 text-sm mr-1"></i>
                        <span className="text-sm text-gray-600">{offer.rating}</span>
                        <span className="text-gray-400 mx-2">•</span>
                        <span className="text-sm text-gray-600">{offer.estimatedTime} دقيقة</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold text-[#F9A826]">{offer.price} ريال</p>
                    {selectedOffer === offer.id && (
                      <i className="ri-check-circle-fill text-[#F9A826] text-lg mt-1"></i>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Confirm Button */}
        {selectedOffer && (
          <Button
            onClick={handleConfirmOffer}
            className="w-full bg-gradient-to-r from-[#F9A826] to-[#E8971F] hover:from-[#E8971F] hover:to-[#D8861E]"
            size="lg"
          >
            <i className="ri-check-line mr-2"></i>
            تأكيد اختيار العرض
          </Button>
        )}

        {/* Tips */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4">
          <div className="flex items-start">
            <i className="ri-lightbulb-line text-blue-600 text-lg mr-3 mt-1"></i>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">نصائح للاختيار</h4>
              <p className="text-blue-600 text-sm">
                اختر العرض الذي يناسب احتياجاتك من حيث السعر والوقت وتقييم السائق
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
